import axios, { AxiosError } from 'axios'
import axiosRetry from 'axios-retry'
import pLimit from 'p-limit'
import toast from 'react-hot-toast'
import { v4 as uuidv4 } from 'uuid'

import { env } from '../env'
import { ProductProblem } from '../interfaces/productProblemsEnum'
import { api } from '../libs/axios'
import { addPendingUpload } from '../utils/indexedDB/addPendingUpload'
import { deletePendingUpload } from '../utils/indexedDB/deletePendingUpload'

axiosRetry(axios, {
  retries: 3,
  retryDelay: (retryCount) => retryCount * 2000,
  retryCondition: (error) => error.code === 'ECONNABORTED' || (error.response!.status >= 500),
})

export interface Report {
  id: string
  store: {
    id: string
    name: string
    state: string
    city: string
    group: {
      id: string
      name: string
      logo_url: string
    },
    seller: {
      id: string
      name: string
      phone: string
      avatar_url: string
    },
    store_promoter: {
      promoter: {
        id: string
        name: string
        phone: string
        avatar_url: string
      }
    }[]
  },
  trade_marketing_check_date: string | Date
  report_product_problem: {
    id: string
    qty?: number
    problem: ProductProblem
    expire_date?: string
    created_at: string
    product: {
      id: string
      name: string
      code: string
    }
  }[]
  manager_observations: string
  improvement_suggestions: string
  general_observations: string
  promoter_notes: string
  has_promoter_problem: boolean
  updated_at: string | Date
  user: {
    id: string
    name: string
    phone: string
    avatar_url?: string
  },
  report_images: {
    id: string
    url: string
    latitude: number
    longitude: number
    user: {
      id: string
      name: string
      phone: string
      avatar_url?: string
    }
    created_at: string | Date
  }[]
  promoters_staying_time?: {
    id: string
    promoter: {
      id: string
      name: string
      phone: string
      avatar_url?: string
    }
    entry_time: Date
    entry_time_coordinates: string
    leave_time: Date
    leave_time_coordinates: string
    expired: boolean
  }[]
}

export interface GetReportsRequest {
  page?: number
  id?: string
  promoter_id?: string
  seller_id?: string
  group_id?: string
  store_id?: string
  has_promoter_problem?: string
  has_product_problem?: string
  city?: string
  state?: string
  order?: 'asc' | 'desc'
  order_by_checked?: string
}

interface GetReportsResponse {
  reports: Report[]
  totalCount: number
  pageQty: number
}

export async function getReports(
  searchParams: GetReportsRequest,
): Promise<GetReportsResponse> {
  try {
    const params: { [key: string]: string | boolean } = {}

    Object.entries(searchParams).forEach(([key, value]) => {
      const param = key.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase()
      params[param] = value
    })

    const result = await api.get<GetReportsResponse>('/reports', {
      params,
    })

    const { data } = result

    return data
  } catch (err) {
    if (err instanceof AxiosError && err.status !== 401) {
      console.error(err.message)
    }
    return {
      reports: [],
      pageQty: 0,
      totalCount: 0,
    }
  }
}

interface CreateReportBody {
  store_id: string
  trade_marketing_check_date?: Date
  product_problems: {
    product: {
      id: string
      name: string
      code?: string
    }
    qty?: number | null
    expire_date?: string | null
  }[]
  manager_observations?: string
  improvement_suggestions?: string
  general_observations?: string
  has_promoter_problem: boolean
}

export async function createReport(body: CreateReportBody, token: string) {
  try {
    const reportBody: { [key: string]: string | boolean } = {}
    Object.entries(body).forEach(([key, value]) => {
      const param = key.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase()
      reportBody[param] = value
    })

    const { data } = await api.post('/reports', {
      ...reportBody,
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })

    return {
      report: data.report,
      success: true,
      message: 'Relatório criado com sucesso',
    }
  } catch (err) {
    if (err instanceof AxiosError && err.status !== 401) {
      return {
        success: false,
        message: err.response?.data.message || `Erro ao criar relatório: ${err.message}`,
      }
    }
  }
}

interface UpdateReportBody {
  id: string
  store_id?: string
  trade_marketing_check_date?: Date
  product_problems?: {
    problem: ProductProblem
    product: {
      id: string
      name: string
      code?: string
    }
    qty?: number | null
    expire_date?: string | null
  }[]
  manager_observations?: string
  improvement_suggestions?: string
  general_observations?: string
  has_promoter_problem?: boolean
}

export async function updateReport(body: UpdateReportBody, token: string) {
  try {
    const reportBody: { [key: string]: string | boolean } = {}
    Object.entries(body).forEach(([key, value]) => {
      const param = key.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase()
      reportBody[param] = value
    })

    const { data } = await api.put(`/reports/${body.id}`, {
      ...reportBody,
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })

    return {
      report: data.report,
      success: true,
      message: 'Relatório atualizado com sucesso',
    }
  } catch (err) {
    if (err instanceof AxiosError) {
      return {
        success: false,
        message: err.response?.data.message || `Erro ao atualizar relatório: ${err.message}`,
      }
    }
  }
}

interface UpdateReportObservationsBody {
  id: string
  manager_observations?: string
  improvement_suggestions?: string
  general_observations?: string
}

export async function updateReportObservations(body: UpdateReportObservationsBody) {
  try {
    const reportBody: { [key: string]: string | boolean } = {}
    Object.entries(body).forEach(([key, value]) => {
      const param = key.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase()
      reportBody[param] = value
    })

    const { data } = await api.patch(`/reports/observations/${body.id}`, {
      manager_observations: body.manager_observations,
      improvement_suggestions: body.improvement_suggestions,
      general_observations: body.general_observations,
    })

    return data.report
  } catch {
    toast.error('Erro ao atualizar observações do relatório')
  }
}

interface UpdateReportPromoterNotesBody {
  id: string
  promoter_notes: string
}

export async function updateReportPromoterNotes(body: UpdateReportPromoterNotesBody) {
  try {
    const { data } = await api.patch(`/reports/promoter-notes/${body.id}`, {
      promoter_notes: body.promoter_notes,
    })

    return data.report
  } catch {
    toast.error('Erro ao atualizar observações do promotor', { position: 'top-left' })
  }
}

export interface FileImage {
  id?: string
  file?: File
  latitude?: number
  longitude?: number
  created_at?: Date
  user: {
    id?: string,
    name?: string,
    avatar_url?: string,
  }
}

interface ProgressCallback {
  (uploaded: number, total: number): void
}

interface NetworkInformation {
  effectiveType?: string
  downlink?: number
}

export async function uploadImages(
  imagesList: FileImage[],
  reportId: string,
  onProgress?: ProgressCallback,
  imagesToRemove?: string[],
  pendingUploadId?: string,
  created_at?: Date,
) {
  const hasNewFile = imagesList.some(item => item.file && item.file.type.includes('image'))
  let filterImagesList: Array<Omit<FileImage, 'file'> & { file: File }> = []
  try {
    if (hasNewFile) {
      filterImagesList = imagesList.filter(item => item.file && item.file.type.includes('image')) as Array<Omit<FileImage, 'file'> & { file: File }>

      if (!pendingUploadId) {
        pendingUploadId = uuidv4()
        addPendingUpload({
          pendingUploadId,
          imagesList: filterImagesList,
          reportId,
          imagesToRemove,
          created_at: new Date(),
        })
      }

      if (filterImagesList.length > 0) {
        const { data: signedUrls } = await api.post<{ urls: { fileName: string, uploadUrl: string }[] }>(
          '/reports/images/get-signed-urls',
          {
            files: filterImagesList.map(({ file }) => ({ fileName: file.name, fileType: file.type })),
            report_id: reportId,
          }, {
            timeout: 60 * 1000,
          },
        )

        const connection = (navigator as unknown as { connection?: NetworkInformation }).connection
        const isSlowConnection = connection && (connection.effectiveType === '2g' || (connection.downlink ?? 10) < 1)

        const limit = pLimit(isSlowConnection
          ? 1
          : 2)
        const totalSize = filterImagesList.reduce((acc, { file }) => acc + file.size, 0)

        const fileProgress: number[] = filterImagesList.map(() => 0)
        const uploadPromises = filterImagesList.map(({ file }, index) => {
          const fileItem = signedUrls.urls[index]

          return limit(() =>
            axios.put(fileItem.uploadUrl, file, {
              headers: {
                'Content-Type': file.type,
              },
              timeout: 2 * 60 * 1000, // 2 minutos
              timeoutErrorMessage: 'Parece que sua conexão está muito lenta. Tente novamente mais tarde.',
              onUploadProgress: (progressEvent) => {
                fileProgress[index] = progressEvent.loaded
                const uploadedBytes = fileProgress.reduce((acc, bytes) => acc + bytes, 0)
                const progress = Math.min((uploadedBytes / totalSize) * 100, 100)
                if (onProgress) {
                  onProgress(progress, 100)
                }
              },
            }),
          )
        })

        await Promise.all(uploadPromises)
        toast('Imagens enviadas', { position: 'bottom-left' })
      }
    }

    await api.patch(
      '/reports/images',
      {
        report_id: reportId,
        images: filterImagesList
          ? filterImagesList.map(item => ({
            fileName: item.file.name,
            latitude: item.latitude,
            longitude: item.longitude,
            user_id: item.user.id,
          }))
          : [],
        to_be_removed: imagesToRemove,
        created_at,
      },
    )

    if (pendingUploadId) {
      await deletePendingUpload(pendingUploadId)
    }
    toast('Registros salvos', { position: 'bottom-left' })
    return {
      success: true,
      message: 'Operação realizada com sucesso',
      error: undefined,
    }
  } catch (error) {
    const err = error as Error
    const message = err.message ?? 'Houve um problema com o upload das images mas elas foram salvas e o sistema tentará reenvia-las automaticamente.'
    return {
      success: false,
      message,
      error: err,
    }
  }
}

export async function getReport(reportId: string, guestToken?: string | null) {
  const { data } = await api.get<{ report: Report }>(`/reports/${reportId}`,
    {
      headers: !guestToken
        ? {}
        : {
            Authorization: `Bearer ${guestToken}`,
          },
    },
  )

  return data.report
}

export async function deleteReport(reportId: string) {
  try {
    const { data } = await api.delete(`/reports/${reportId}`)

    return {
      reportId: data.reportId,
      success: true,
      message: 'Relatório excluído com sucesso',
    }
  } catch (err) {
    if (err instanceof AxiosError) {
      return {
        success: false,
        message: err.response?.data.message || `Erro ao excluir relatório: ${err.message}`,
      }
    }
  }
}

interface ToggleHasPromoterProblemResponse {
  report: Report
}

export async function toggleHasPromoterProblem(reportId: string) {
  try {
    const { data } = await api.patch<ToggleHasPromoterProblemResponse>(`/reports/toggle-promoter-problem/${reportId}`)

    return {
      report: data.report,
      success: true,
      message: 'Status de promoção alterado com sucesso',
    }
  } catch (err) {
    if (err instanceof AxiosError) {
      return {
        success: false,
        message: err.response?.data.message || `Erro ao alterar status de promoção: ${err.message}`,
      }
    }
  }
}

interface CheckProductProblemsResponse {
  reportProductProblems: Report['report_product_problem']
}

export async function checkProductProblems(product_problem_id: string, report_id: string): Promise<CheckProductProblemsResponse | void> {
  try {
    const { data } = await api.patch<CheckProductProblemsResponse>('/reports/check-product-problem', {}, {
      params: {
        product_problem_id,
        report_id,
      },
    })

    return data
  } catch (err) {
    if (err instanceof AxiosError && err.status !== 401) {
      alert(err.response?.data.message)
    }
  }
}

export interface CreatePromoterStayingTimeRegisterRequest {
  entryTime: Date
  entryTimeCoordinates: string
  reportId: string
  userId: string
  device?: string
}

interface CreatePromoterStayingTimeRegisterResponse {
  promoterStayingTime: {
    id: string,
  }
}

export async function createPromoterStayingTime(body: CreatePromoterStayingTimeRegisterRequest) {
  try {
    const reportBody: { [key: string]: string | boolean } = {}
    Object.entries(body).forEach(([key, value]) => {
      const param = key.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase()
      reportBody[param] = value
    })

    const { data } = await api.post<CreatePromoterStayingTimeRegisterResponse>('/reports/promoters-staying-time', reportBody)

    return data
  } catch (err) {
    if (err instanceof AxiosError && err.status !== 401) {
      alert(`Erro ao criar registro: ${err.response?.data.message}`)
    }
  }
}

export interface ClosePromoterStayingTimeRegisterRequest {
  id: string
  leaveTime: Date
  leaveTimeCoordinates: string
}

interface ClosePromoterStayingTimeRegisterResponse {
  promoterStayingTime: {
    id: string,
  }
}

export async function closePromoterStayingTime(body: ClosePromoterStayingTimeRegisterRequest) {
  try {
    const reportBody: { [key: string]: string | boolean } = {}
    Object.entries(body).forEach(([key, value]) => {
      const param = key.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase()
      reportBody[param] = value
    })

    const { data } = await api.patch<ClosePromoterStayingTimeRegisterResponse>('/reports/promoters-staying-time/close', reportBody)

    return data
  } catch (err) {
    if (err instanceof AxiosError && err.status !== 401) {
      alert(`Erro ao encerrar registro: ${err.response?.data.message}`)
    }
  }
}

export async function deletePromoterStayinTimeRegister(id: string, token: string) {
  try {
    await api.delete(`/reports/promoters-staying-time/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
  } catch (err) {
    if (err instanceof AxiosError && err.status !== 401) {
      alert(`Erro ao deletar registro: ${err.response?.data.message}`)
    }
  }
}

interface CheckExistingOpenPromoterStayinTimeRegisterResponse {
  promoterStayingTime: {
    id: string
    device: string
    entry_time: string
    report_id: string
    entry_time_coordinates: string
    leave_time?: string
    leave_time_coordinates?: string
    promoter_id: string
  }
}

export async function checkExistingOpenPromoterStayingTimeRegister() {
  try {
    const result = await api.get<CheckExistingOpenPromoterStayinTimeRegisterResponse>('/reports/promoters-staying-time/check_open')

    return result.data.promoterStayingTime
  } catch (err) {
    if (err instanceof AxiosError && err.status !== 401) {
      alert(`Erro ao recuperar registro: ${err.response?.data.message}`)
    }
  }
}

export async function updatePromoterStayingTimeDevice(stayingTimeId: string, device: string) {
  try {
    await api.patch(`/reports/promoters-staying-time/${stayingTimeId}`, {
      device,
    })
  } catch (err) {
    if (err instanceof AxiosError && err.status !== 401) {
      alert(`Erro ao atualizar dispositivo do registro: ${err.response?.data.message}`)
    }
  }
}

interface AddProductProblemRequest {
  report_id: string
  problem: ProductProblem
  product: {
    id: string
    name: string
    code?: string
  }
  qty?: number | null
  expire_date?: string | null
}

export async function addProductProblem(data: AddProductProblemRequest) {
  await api.patch('/reports/add-product-problems', data)
}

export interface CleanReport {
  id: string
  store: {
    name: string
    state: string
    city: string
    group: {
      name: string
      logo_url: string
    },
    seller: {
      name: string
    },
    store_promoter: {
      promoter: {
        name: string
      }
    }[]
  },
  report_product_problem: {
    qty?: number
    problem: ProductProblem
    expire_date?: string
    product: {
      name: string
      code: string
    }
  }[]
  manager_observations: string
  improvement_suggestions: string
  general_observations: string
  trade_marketing_check_date: string | Date
  promoter_notes: string
  has_promoter_problem: boolean
  updated_at: string | Date
}

export interface GetReportsWithoutPaginationRequest {
  id?: string
  promoter_id?: string
  seller_id?: string
  group_id?: string
  store_id?: string
  has_promoter_problem?: string
  has_product_problem?: string
  city?: string
  state?: string
}

interface GetReportsWithoutPaginationResponse {
  reports: CleanReport[]
}

export async function getReportsWithoutPagination(
  searchParams: GetReportsWithoutPaginationRequest,
): Promise<GetReportsWithoutPaginationResponse> {
  try {
    const params: { [key: string]: string | boolean } = {}

    Object.entries(searchParams).forEach(([key, value]) => {
      const param = key.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase()
      params[param] = value
    })

    const result = await api.get<GetReportsResponse>('/reports/no-page', {
      params,
    })

    const { data } = result

    return data
  } catch (err) {
    if (err instanceof AxiosError && err.status !== 401) {
      console.error(err.message)
    }
    return {
      reports: [],
    }
  }
}

interface ToggleTradeMarketingDailyCheckResponse {
  report: {
    id: string
    trade_marketing_check_date: Date | string
  }
}

export async function toggleTradeMarketingDailyCheck(reportId: string) {
  try {
    const { data } = await api.patch<ToggleTradeMarketingDailyCheckResponse>(`/reports/check-trade-marketing-daily/${reportId}`)

    return {
      report: data.report,
      success: true,
      message: 'Relatório marcado como "Verificado"',
    }
  } catch (err) {
    if (err instanceof AxiosError) {
      return {
        success: false,
        message: err.response?.data.message || `Erro ao alterar status: ${err.message}`,
      }
    }
  }
}

export async function generateTemporaryAccessLink(reportId: string) {
  try {
    const { data } = await api.post<{ guestAccessToken: string }>('/temporary-access')

    const temporaryLink = `${env.VITE_APP_BASE_URL}/temporary-access/${reportId}?guestTokenAccess=${data.guestAccessToken}`
    return {
      success: true,
      message: 'Link de acesso temporário gerado com sucesso',
      data: temporaryLink,
    }
  } catch (err) {
    if (err instanceof AxiosError) {
      return {
        success: false,
        message: err.response?.data.message || `Erro ao gerar link de acesso temporário: ${err.message}`,
      }
    }
  }
}
